import { useState, useEffect } from "react";
import { Autocomplete } from "./";
import { useLead } from "../hooks/query";

interface LeadSource {
    Id: string;
    Name: string;
    Description?: string;
    IsActive?: boolean;
}

interface LeadSourceDropdownProps {
    name: string;
    label: string;
    placeholder?: string;
    rules?: object;
    helperText?: string;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
}

function LeadSourceDropdown({
    name,
    label,
    placeholder = "Select lead source",
    rules = {},
    helperText,
    size = "md",
    disabled = false,
    ...props
}: LeadSourceDropdownProps) {
    const { useListLeadSources } = useLead();
    const listLeadSources = useListLeadSources();

    const [leadSources, setLeadSources] = useState<LeadSource[]>([]);

    // Fetch lead sources on component mount
    useEffect(() => {
        const fetchLeadSources = async () => {
            try {
                const response = await listLeadSources.mutateAsync();
                if (response && Array.isArray(response)) {
                    setLeadSources(response);
                }
            } catch (error) {
                console.error("Error fetching lead sources:", error);
                setLeadSources([]);
            }
        };

        fetchLeadSources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Transform lead sources for autocomplete
    const leadSourceOptions = leadSources.map((source) => ({
        value: source.Id,
        label: source.Name,
        ...source,
    }));

    return (
        <Autocomplete
            name={name}
            label={label}
            placeholder={placeholder}
            rules={rules}
            helperText={helperText}
            size={size}
            options={leadSourceOptions}
            disabled={disabled || listLeadSources.isPending}
            loading={listLeadSources.isPending}
            isOptionEqualToValue={(option: any, value: any) => {
                if (!option || !value) return false;
                return option.value === value.value;
            }}
            getOptionLabel={(option: any) => {
                if (typeof option === "string") return option;
                return option.label ?? "";
            }}
            {...props}
        />
    );
}

export { LeadSourceDropdown };
